import { computed, ref } from 'vue';
import { useAppStateStore } from '@/stores';

const DEVICE_STATUS_REGEX = /:(\w+)/g;
const OFFLINE_STATUS_MAPPINGS = ['unknown', 'error', 'invalidCredentials', 'deviceOffline'];

type ActionType = 'bridge' | 'camera';

export function useDeviceStatusFilter() {
  const appStateStore = useAppStateStore();

  const searchQueryLower = computed(() => appStateStore.searchQuery.toLowerCase());
  const isDeviceStatusFilterEnabled = computed(() => {
    // Check if the search query contains any device status indicators
    const hasDeviceStatusFilter = searchQueryLower.value.match(DEVICE_STATUS_REGEX);

    return !!hasDeviceStatusFilter?.length;
  });

  function getParsedStatusesFromQuery(searchQuery: string): { finalStatuses: string[]; deviceType: ActionType | '' } {
    const statuses: string[] = [];
    let deviceType: ActionType | '' = '';

    // Split the search query into parts based on whitespace
    const queryParts = searchQuery.trim().split(/\s+/);

    for (const query of queryParts) {
      if (query.startsWith('device_type:')) {
        // Extract the device type from the query
        const type = query.split(':')[1];
        if (type === 'camera' || type === 'bridge') {
          deviceType = type;
        }
        // extract the statuses from query
      } else if (query.startsWith(':')) {
        statuses.push(query.slice(1).trim());
      }
    }

    let finalStatuses = [...statuses];

    if (finalStatuses.includes('offline')) {
      finalStatuses = finalStatuses.filter((status) => status !== 'offline').concat(OFFLINE_STATUS_MAPPINGS);
    }

    if (finalStatuses.includes('internetoffline')) {
      finalStatuses = finalStatuses.filter((status) => status !== 'internetoffline').concat(['bridgeOffline']);
    }

    return {
      finalStatuses,
      deviceType,
    };
  }

  /**
   * Generates device status filter parameters based on the search query and action type.
   *
   * @param {ActionType} type - The action type ('camera' or 'bridge').
   * @returns {Record<string, string>} - Query parameters for device status filtering.
   */
  const getDeviceStatusFilterParams = (type: ActionType): Record<string, string> => {
    const { finalStatuses, deviceType } = getParsedStatusesFromQuery(searchQueryLower.value);

    // Update store based on the device type
    if (deviceType) {
      appStateStore.deviceStatusFilterTypes = [deviceType];
    } else {
      appStateStore.deviceStatusFilterTypes = [];
    }

    // If device type is not specified but statuses are present, allow filtering by statuses only
    if (!appStateStore.deviceStatusFilterTypes.length && finalStatuses.length > 0) {
      return { status__in: finalStatuses.join(',') };
    }

    if (!isDeviceStatusFilterEnabled.value || !appStateStore.deviceStatusFilterTypes.length || type !== deviceType) {
      return {};
    }

    // Return the filter parameters if statuses are available
    return finalStatuses.length > 0 ? { status__in: finalStatuses.join(',') } : {};
  };

  return {
    getParsedStatusesFromQuery,
    getDeviceStatusFilterParams,
    isDeviceStatusFilterEnabled,
  };
}
